import { Business, PurchaseReceipt } from "shared/types";
import { DiscountSource, TaxMode } from "shared/types/enum";
import { formatTransactionNumber } from "./transactionNumber";
import { saveBlobAsFile } from "./download";
import { loadReportPdf } from "./loadPdfChunk";

// Builds the invoice/receipt data straight from the purchase row already
// loaded on screen, so no extra fetch is needed. One caller: the customer's
// package/membership detail page (CustomerPackageDetails), which is also the
// only place either document can be downloaded. An earlier version of this
// comment also claimed the business-wide Pembelian report row as a caller;
// that surface has never had a download.
export const purchaseReceiptFromParts = (parts: {
  transactionNumber: number | null;
  business: Business | null | undefined;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  itemName: string;
  price: number;
  upgradeFullPrice?: number | null;
  upgradeFromName?: string | null;
  upgradePreviousPrice?: number | null;
  purchasedAt: string;
  paymentMethodName: string | null;
  discountSource?: DiscountSource | null;
  discountCode?: string | null;
  discountPercent?: number | null;
  discountAmount?: number | null;
  originalPrice?: number | null;
  taxEnabled?: boolean;
  taxMode?: TaxMode | null;
  taxRate?: number | null;
  taxLabel?: string | null;
  voidedAt?: string | null;
}): PurchaseReceipt => ({
  transactionNumber: parts.transactionNumber,
  // Prefer the formal/registered name on official paperwork; falls back to
  // the everyday trading name for every business that hasn't set one.
  businessName: parts.business?.legal_name || parts.business?.name || "",
  businessAddress: parts.business?.address ?? "",
  customerName: parts.customerName,
  customerPhone: parts.customerPhone,
  customerEmail: parts.customerEmail,
  itemName: parts.itemName,
  price: parts.price,
  upgradeFullPrice: parts.upgradeFullPrice ?? null,
  upgradeFromName: parts.upgradeFromName ?? null,
  upgradePreviousPrice: parts.upgradePreviousPrice ?? null,
  purchasedAt: parts.purchasedAt,
  paymentMethodName: parts.paymentMethodName,
  discountSource: parts.discountSource ?? null,
  discountCode: parts.discountCode ?? null,
  discountPercent: parts.discountPercent ?? null,
  discountAmount: parts.discountAmount ?? null,
  originalPrice: parts.originalPrice ?? null,
  // From the purchase's OWN snapshot, passed in by the caller — never the
  // business's current settings.
  taxEnabled: parts.taxEnabled ?? false,
  taxMode: parts.taxMode ?? null,
  taxRate: parts.taxRate ?? null,
  taxLabel: parts.taxLabel ?? null,
  npwp: parts.business?.npwp ?? null,
  // A voided sale is still documented: the caller passes the row's voided_at
  // so the document itself says the number was cancelled, instead of a valid
  // looking invoice circulating for a sale that no report counts.
  voidedAt: parts.voidedAt ?? null,
});

export const downloadPurchaseDocument = async (
  kind: "invoice" | "receipt",
  data: PurchaseReceipt,
  studio: string,
) => {
  const { renderReportPdf } = await loadReportPdf();
  const blob = await renderReportPdf(
    kind === "invoice"
      ? { slug: "invoice-pembelian", studio, data }
      : { slug: "kwitansi-pembelian", studio, data },
  );
  const filePrefix = kind === "invoice" ? "Invoice" : "Kwitansi";
  const numberPrefix = kind === "invoice" ? "INV" : "RCP";
  saveBlobAsFile(
    blob,
    `${filePrefix}_${formatTransactionNumber(numberPrefix, data.transactionNumber)}.pdf`
  );
};
